/* eslint-disable */
/**
 * Land chat drawer and barbarian raid assets — neither folder was in the blob preload manifest
 * at all, so every image in both was reaching the player on demand, mid-interaction.
 *
 * Both surfaces open straight off the land page: the chat drawer from its own nav button, the
 * barbarian modal from the raid alert that appears over the land page while today's barbarians
 * are still standing. Opening either one cold meant watching the panel assemble itself — which
 * reads as broken rather than slow, the same reasoning as `landGateExtrasPreloadUrls.ts`.
 *
 * Gated (see `GATE_EXACT_URLS` in `vercelBlobPreloadUrls.ts`), except the one file called out
 * below. Every URL here was checked against the CDN and returns 200.
 */

const BLOB = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com'

/**
 * The chat drawer — `pages/Main/components/LandChat.tsx` + `LandChat.css` and the panels it
 * hosts (`BotTab`, `SupportTab`, `SupportFaqPanel`, `SupportFeedbackPanel`).
 *
 * Roughly 280 KB for the set, so the whole drawer is warm for the cost of one mid-sized image.
 *
 * NOTE: the sidebar tile plates are listed as explicit `<name>_active` / `<name>_inactive` pairs
 * because `SupportTab.tsx` builds them at runtime (`tileUrl`) out of a base constant, so nothing
 * that scans for literal URLs can see them. `guide_active.webp` IS included even though the
 * comment in that file notes the Guide tile never lights: it exists on the CDN, it costs 1.8 KB,
 * and listing it keeps the pair rule simple if the Guide ever becomes a selectable section.
 */
export const LAND_CHAT_PRELOAD_URLS: readonly string[] = [
  // Drawer frame — header, body, footer, tab strip.
  `${BLOB}/chat/chat-header.webp`,
  `${BLOB}/chat/chat_body.webp`,
  `${BLOB}/chat/header-bg.webp`,
  `${BLOB}/chat/footer.webp`,
  `${BLOB}/chat/board.webp`,
  `${BLOB}/chat/tab_active.webp`,
  `${BLOB}/chat/tab_inactive.webp`,
  // Conversation / DM sidebar rows.
  `${BLOB}/chat/sidebar_item.webp`,
  `${BLOB}/chat/sidebar_activeitem.webp`,
  // Composer.
  `${BLOB}/chat/inputbox.webp`,
  `${BLOB}/chat/btn_send.webp`,
  `${BLOB}/chat/btn_emoticon.webp`,
  `${BLOB}/chat/btn_upload.webp`,
  `${BLOB}/chat/btn_back.webp`,
  // Coloured action buttons on invite / confirm rows.
  `${BLOB}/chat/btn_red.webp`,
  `${BLOB}/chat/btn_green.webp`,
  `${BLOB}/chat/btn_blue.webp`,
  // Support tab — rail, tile plates, section headers.
  `${BLOB}/chat/supportsidebar.webp`,
  `${BLOB}/chat/faq_inactive.webp`,
  `${BLOB}/chat/faq_active.webp`,
  `${BLOB}/chat/ai_inactive.webp`,
  `${BLOB}/chat/ai_active.webp`,
  `${BLOB}/chat/guide_inactive.webp`,
  `${BLOB}/chat/guide_active.webp`,
  `${BLOB}/chat/feedback_inactive.webp`,
  `${BLOB}/chat/feedback_active.webp`,
  `${BLOB}/chat/faq_header.webp`,
  `${BLOB}/chat/feedback_header.webp`,
  `${BLOB}/chat/support_Info.webp`,
  // Ask-to-AI bot tab.
  `${BLOB}/chat/bot_logo.webp`,
  `${BLOB}/chat/icon_bot.webp`,
  `${BLOB}/chat/pen_icon.webp`,
  `${BLOB}/chat/suggest_box1.webp`,
  `${BLOB}/chat/suggest_box2.webp`,
  `${BLOB}/chat/suggest_box3.webp`,
]

/**
 * `chat/btn_remove.png` — the X on an attachment thumbnail (`LandChat.css`).
 *
 * Deliberately NOT gated. It is 1.34 MB: a full-size PNG doing the job of a ~1 KB icon, and on
 * its own it is five times the weight of the entire gated chat set above. It is also the one
 * chat asset that cannot be seen without first picking a file to upload, so nothing is broken
 * by it arriving a moment later. Listed here so the idle sweep still warms it.
 *
 * Fix the asset (`npm run audit:assets`) and it can move into the list above.
 */
export const LAND_CHAT_IDLE_PRELOAD_URLS: readonly string[] = [
  `${BLOB}/chat/btn_remove.png`,
]

/**
 * Barbarian daily raid — `components/Header/BarbariansModal.tsx` and `BarbarianRaidAlert.tsx`.
 *
 * The whole set is 22 KB. These moved off `public/assets/babarians/` (note the folder's
 * misspelling) onto the blob, and the blob path is spelled correctly.
 */
export const BARBARIANS_PRELOAD_URLS: readonly string[] = [
  `${BLOB}/barbarians/modal_bg.webp`,
  `${BLOB}/barbarians/btn_attack.webp`,
  `${BLOB}/barbarians/icon_attack.webp`,
  `${BLOB}/barbarians/icon_sandclock.webp`,
  `${BLOB}/barbarians/icon_info.webp`,
  `${BLOB}/barbarians/icon_note.webp`,
]

/** Everything above, for the manifest. */
export const LAND_CHAT_BARBARIANS_PRELOAD_URLS: readonly string[] = [
  ...LAND_CHAT_PRELOAD_URLS,
  ...LAND_CHAT_IDLE_PRELOAD_URLS,
  ...BARBARIANS_PRELOAD_URLS,
]
